import { Color3, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from './Asset';
import { AssetObject } from './AssetObject';
import { ObstacleConfig } from './ObstacleGenerator';

export interface ObstaclePrefabConfig {
  probabilityMin: number;
  probabilityMid: number;
  probabilityMax: number;
  minPoint: number;
  obstacleColor: string;
  pathWidth: number;
  spawnRange: number;
  despawnRange: number;
}

export class ObstaclePrefabs {
  static create(config: ObstaclePrefabConfig): ObstacleConfig {
    const pathHalfWidth = config.pathWidth / 2;

    const obstacleConfig: ObstacleConfig = {
      assets: [
        new Asset('spike-block-wide', AssetType.MESH, 'assets/model/platformer/', 'spike-block-wide.glb'),
        new Asset('spike-block', AssetType.MESH, 'assets/model/platformer/', 'spike-block.glb'),
        new Asset('axe', AssetType.MESH, 'assets/model/survival/', 'tool-axe-upgraded.glb'),
        new Asset('campfire-pit', AssetType.MESH, 'assets/model/survival/', 'campfire-pit.glb'),
        new Asset('barricade-1', AssetType.MESH, 'assets/model/building/', 'barricade-window-b.glb'),
        new Asset('barricade-2', AssetType.MESH, 'assets/model/building/', 'barricade-window-c.glb'),
        new Asset('banner', AssetType.MESH, 'assets/model/arena/', 'banner.glb'),
        new Asset('campfire-stones', AssetType.MESH, 'assets/model/nature/', 'campfire_stones.glb'),
        new Asset('rocket', AssetType.MESH, 'assets/model/space/', 'craft_racer.glb'),
        new Asset(
          'swiper',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'swiper_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'swiper-double',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'swiper_double_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'swiper-quad',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'swiper_quad_long_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'hammer',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'hammer_large_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'hammer-spikes',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'hammer_spikes_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'cannon',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'cannon_base_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'floor-spikes-trap',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'floor_spikes_trap_4x4x1_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'saw',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'saw_trap_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'spike-block-omni',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'spikeblock_omni_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'spike-block-standing',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'spikeblock_double_horizontal_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'spring-pad',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'spring_pad_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'arch-wide',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'arch_wide_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'pipe-90',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'pipe_90_B_' + config.obstacleColor + '.gltf'
        ),
        new Asset(
          'chest',
          AssetType.MESH,
          'assets/model/platformer_shiny/' + config.obstacleColor + '/',
          'chest_' + config.obstacleColor + '.gltf'
        ),
      ],
      assetObjects: [
        new AssetObject('spike-block-wide').withType('spike-block-wide'),
        new AssetObject('spike-block').withMaterialProperties(1, 0.5, new Color3(2, 2, 2)).withType('spike-block'),
        new AssetObject('axe').withType('axe'),
        new AssetObject('campfire-pit').withType('campfire-pit'),
        new AssetObject('barricade-1').withType('barricade'),
        new AssetObject('barricade-2').withType('barricade'),
        new AssetObject('saw').withType('saw'),
        new AssetObject('bomb').withType('bomb'),
        new AssetObject('rocket').withType('rocket').withComputeCenter(true),
        new AssetObject('swiper').withType('swiper'),
        new AssetObject('swiper-double').withRoughness(1).withType('swiper-double'),
        new AssetObject('swiper-quad').withType('swiper-quad'),
        new AssetObject('hammer').withType('hammer'),
        new AssetObject('hammer-spikes').withType('hammer-spikes'),
        new AssetObject('cannon').withType('cannon'),
        new AssetObject('banner').withType('banner'),
        new AssetObject('floor-spikes-trap').withType('floor-spikes-trap'),
        new AssetObject('campfire-stones').withType('campfire-stones'),
        new AssetObject('spike-block-omni').withType('spike-block-omni'),
        new AssetObject('spike-block-standing').withType('spike-block-standing'),
        new AssetObject('spring-pad').withType('spring-pad'),
        new AssetObject('arch-wide').withType('arch-wide'),
        new AssetObject('pipe-90').withType('pipe-90'),
        new AssetObject('chest').withType('chest'),
      ],
      distributions: [
        {
          types: ['chest'],
          behavior: 'magnet',
          probability: config.probabilityMin,
          yOffset: 0,
          offsetRange: { min: -pathHalfWidth, max: pathHalfWidth },
          scale: { min: 1.5, max: 1.5 },
          useLevelYPosition: true,
          minPoint: config.minPoint,
        },
        {
          types: ['pipe-90'],
          behavior: 'windZone',
          probability: config.probabilityMin,
          yOffset: 0,
          offsetRange: { min: 6, max: 7 },
          scale: { min: 0.5, max: 0.5 },
          axis: 'z',
          direction: 1,
          harmless: true,
          minPoint: config.minPoint,
        },
        {
          types: ['cannon'],
          behavior: 'turret',
          probability: config.probabilityMin,
          yOffset: 0,
          scale: { min: 0.6, max: 0.6 },
          speed: 18,
          minPoint: config.minPoint,
        },
        {
          types: ['arch-wide'],
          behavior: 'laserGrid',
          probability: config.probabilityMin,
          rotation: new Vector3(0, Math.PI / 2, 0),
          yOffset: -1,
          scale: { min: 2.5, max: 2.5 },
          height: 0.5,
          width: 5,
          speed: 0.5,
          harmless: true,
          minPoint: config.minPoint,
        },
        {
          types: ['spike-block-standing'],
          behavior: 'spikeRing',
          probability: config.probabilityMin,
          yOffset: 1,
          scale: { min: 0.5, max: 0.5 },
          minPoint: config.minPoint,
        },
        {
          types: ['spring-pad'],
          behavior: 'bouncePad',
          probability: config.probabilityMin,
          yOffset: 0,
          scale: { min: 1, max: 1 },
          harmless: true,
          minPoint: config.minPoint,
        },
        {
          types: ['campfire-stones'],
          behavior: 'geyser',
          probability: config.probabilityMin,
          yOffset: -1,
          scale: { min: pathHalfWidth / 2, max: pathHalfWidth / 2 },
          minPoint: config.minPoint,
        },
        {
          types: ['banner'],
          behavior: 'seekerBeam',
          probability: config.probabilityMin,
          scale: { min: 2, max: 2 },
          minPoint: config.minPoint,
        },
        {
          types: ['cannon'],
          behavior: 'cannon',
          probability: config.probabilityMin,
          scale: { min: 0.5, max: 0.5 },
          speed: 9, // projectile speed
          fireInterval: 6,
          fireActiveTime: 10,
          minPoint: config.minPoint,
        },
        {
          types: ['hammer'],
          behavior: 'hammer',
          //rotation: new Vector3(0, Math.PI / 2, 0),
          probability: config.probabilityMin,
          scale: { min: 0.3, max: 0.3 },
          height: 4, // jump height
          minPoint: config.minPoint,
        },
        {
          types: ['barricade'],
          behavior: 'idle',
          scale: { min: 1.5, max: 1.5 },
          probability: config.probabilityMin,
          yOffset: 0,
          alignToPath: true,
          lanes: [0],
          pattern: {
            preset: {
              type: 'horizontalLine',
              segments: 3,
              width: 10,
              height: 5,
            },
          },
          minPoint: config.minPoint,
        },

        {
          types: ['campfire-pit'],
          behavior: 'fire',
          scale: { min: 2, max: 2 },
          probability: config.probabilityMin,
          yOffset: -0.1,
          pattern: { lanes: [-1, 1] },
          axis: 'y',
          fireInterval: 2,
          fireActiveTime: 1,
          fireHeight: 3,
          direction: -1,
          fireWidth: 0.1,
          fireEmitterGrow: true,
          minPoint: config.minPoint,
        },
        {
          types: ['axe'],
          behavior: 'swing',
          scale: { min: 10, max: 10 },
          probability: config.probabilityMin,
          angle: 1.0,
          rotation: new Vector3(0, 0, Math.PI),
          axis: 'z',
          yOffset: 3,
          minPoint: config.minPoint,
        },
        {
          types: ['saw'],
          behavior: 'roll',
          width: pathHalfWidth,
          speed: 1,
          scale: { min: 0.5, max: 0.5 },
          probability: config.probabilityMin,
          bakeRotation: new Vector3(Math.PI / 2, 0, 0),
          alignToPath: true,
          minPoint: config.minPoint,
        },

        {
          types: ['swiper'],
          behavior: 'rotate',
          scale: { min: 0.9, max: 0.9 },
          probability: config.probabilityMin,
          speed: 2,
          yOffset: 0,
          minPoint: config.minPoint,
        },

        {
          types: ['swiper-double'],
          behavior: 'rotate',
          scale: { min: 0.9, max: 0.9 },
          probability: config.probabilityMin,
          speed: 2,
          yOffset: 0,
          minPoint: config.minPoint,
        },

        {
          types: ['swiper-quad'],
          behavior: 'rotate',
          scale: { min: 0.9, max: 0.9 },
          probability: config.probabilityMin,
          speed: 2,
          yOffset: 0,
          minPoint: config.minPoint,
        },

        {
          types: ['spike-block-wide'],
          behavior: 'rotate',
          probability: config.probabilityMin,
          speed: 2,
          yOffset: 1,
          pattern: { lanes: [-1, 1] },
          minPoint: config.minPoint,
        },

        {
          types: ['spike-block'],
          behavior: 'horizontal',
          radius: 1.5,
          speed: 1.5,
          yOffset: 1,
          probability: config.probabilityMin,
          pattern: { lanes: [-1, 1] },
          sound: {
            name: 'obstaclemotor',
            autoplay: true,
          },
          minPoint: config.minPoint,
        },

        {
          types: ['rocket'],
          behavior: 'rocket',
          offsetRange: { min: -2, max: 2 },
          yOffset: 1,
          radius: 5,
          speed: 4,
          rotation: new Vector3(0, Math.PI, 0),
          scale: { min: 2, max: 2 },
          probability: config.probabilityMin,
          minPoint: config.minPoint,
        },

        {
          types: ['spike-block-standing'],
          behavior: 'jump',
          height: 2,
          speed: 1.5,
          probability: config.probabilityMin,
          yOffset: 0.35,
          scale: { min: 0.5, max: 0.5 },
          lanes: [0],
          pattern: { lanes: [-1, 1] },
          minPoint: config.minPoint,
        },

        {
          types: ['floor-spikes-trap'],
          behavior: 'scale',
          scaleRange: { min: 0.5, max: 1.2 },
          probability: config.probabilityMin,
          offsetRange: { min: -2, max: 2 },
          yOffset: -0.85,
          minPoint: config.minPoint,
        },

        {
          types: ['floor-spikes-trap'],
          behavior: 'vertical',
          scale: { min: 0.5, max: 0.5 },
          speed: 1,
          probability: config.probabilityMin,
          offsetRange: { min: -2, max: 2 },
          yOffset: -2,
          minPoint: config.minPoint,
        },
      ],
      spawnRange: config.spawnRange,
      despawnRange: config.despawnRange,
    };

    return obstacleConfig;
  }
}
